import { Controller } from "stimulus"

const language = {
  length: "Rādīt",
  records: "ierakstus",
  zeroRecords: "Nav atrasti vaicājumam atbilstoši ieraksti",
  info: "Parādīti %{start}. līdz %{end}. no %{total} ierakstiem",
  infoEmpty: "Nav ierakstu",
  infoFiltered: "(atlasīts no pavisam %{max} ierakstiem)",
  search: "Meklēt:",
  previous: "Iepriekšējā",
  next: "Nākošā"
}

export default class extends Controller {
  connect() {
    this.page = 1
    this.perPage = 10
    this.sortIndex = null
    this.sortDirection = "asc"
    this.rows = Array.from(this.element.tBodies[0].rows)
    this.headerListeners = []

    this.buildControls()
    this.bindHeaders()
    this.render()
  }

  disconnect() {
    this.headerListeners.forEach(({ header, event, handler }) => header.removeEventListener(event, handler))
    if (this.element.classList.contains("ui-table")) {
      Array.from(this.element.tHead.rows[0].cells).forEach(header => {
        header.removeAttribute("tabindex")
        header.removeAttribute("aria-sort")
      })
    }
    if (this.controls) this.controls.remove()
    if (this.footer) this.footer.remove()
  }

  buildControls() {
    this.controls = document.createElement("div")
    this.controls.className = "datatable-controls"

    const lengthLabel = document.createElement("label")
    lengthLabel.className = "datatable-length"
    lengthLabel.append(`${language.length} `)

    this.lengthSelect = document.createElement("select")
    this.lengthSelect.className = "form-control form-control-sm"
    ;[10, 25, 50, 100].forEach((value) => {
      const option = document.createElement("option")
      option.value = value
      option.textContent = value
      this.lengthSelect.appendChild(option)
    })
    lengthLabel.appendChild(this.lengthSelect)
    lengthLabel.append(` ${language.records}`)

    const searchLabel = document.createElement("label")
    searchLabel.className = "datatable-search"
    searchLabel.append(`${language.search} `)

    this.searchInput = document.createElement("input")
    this.searchInput.className = "form-control form-control-sm"
    this.searchInput.type = "search"
    searchLabel.appendChild(this.searchInput)

    this.controls.append(lengthLabel, searchLabel)
    this.element.parentNode.insertBefore(this.controls, this.element)

    this.footer = document.createElement("div")
    this.footer.className = "datatable-footer"
    this.info = document.createElement("div")
    this.pagination = document.createElement("div")
    this.pagination.className = "datatable-pagination"
    this.footer.append(this.info, this.pagination)
    this.element.after(this.footer)

    this.lengthSelect.addEventListener("change", () => {
      this.perPage = Number(this.lengthSelect.value)
      this.page = 1
      this.render()
    })

    this.searchInput.addEventListener("input", () => {
      this.page = 1
      this.render()
    })
  }

  bindHeaders() {
    Array.from(this.element.tHead.rows[0].cells).forEach((header, index) => {
      if (header.dataset.sortable === "false") return
      header.classList.add("datatable-sortable")
      const sort = () => {
        if (this.sortIndex === index) {
          this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc"
        } else {
          this.sortIndex = index
          this.sortDirection = "asc"
        }

        this.render()
        if (this.element.classList.contains("ui-table")) {
          Array.from(this.element.tHead.rows[0].cells).forEach(cell => cell.removeAttribute("aria-sort"))
          header.setAttribute("aria-sort", this.sortDirection === "asc" ? "ascending" : "descending")
        }
      }
      header.addEventListener("click", sort)
      this.headerListeners.push({ header, event: "click", handler: sort })
      if (this.element.classList.contains("ui-table")) {
        header.tabIndex = 0
        const keydown = event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            sort()
          }
        }
        header.addEventListener("keydown", keydown)
        this.headerListeners.push({ header, event: "keydown", handler: keydown })
      }
    })
  }

  render() {
    const rows = this.sortedRows(this.filteredRows())
    const pageCount = Math.max(Math.ceil(rows.length / this.perPage), 1)
    this.page = Math.min(this.page, pageCount)

    const start = (this.page - 1) * this.perPage
    const visibleRows = rows.slice(start, start + this.perPage)

    this.rows.forEach((row) => {
      row.style.display = visibleRows.includes(row) ? "" : "none"
    })

    this.renderInfo(rows.length, start, visibleRows.length)
    this.renderPagination(pageCount)
  }

  filteredRows() {
    const search = this.searchInput.value.trim().toLowerCase()
    if (!search) return this.rows

    return this.rows.filter((row) => row.textContent.toLowerCase().includes(search))
  }

  sortedRows(rows) {
    if (this.sortIndex == null) return rows

    return [...rows].sort((firstRow, secondRow) => {
      const first = this.cellText(firstRow, this.sortIndex)
      const second = this.cellText(secondRow, this.sortIndex)
      const firstNumber = Number(first)
      const secondNumber = Number(second)
      const comparison = Number.isNaN(firstNumber) || Number.isNaN(secondNumber)
        ? first.localeCompare(second, "lv")
        : firstNumber - secondNumber

      return this.sortDirection === "asc" ? comparison : -comparison
    })
  }

  renderInfo(total, start, visibleCount) {
    if (total === 0) {
      this.info.textContent = this.searchInput.value ? `${language.zeroRecords} ${this.filteredLabel}` : language.infoEmpty
      return
    }

    this.info.textContent = language.info
      .replace("%{start}", start + 1)
      .replace("%{end}", start + visibleCount)
      .replace("%{total}", total) + this.filteredLabel
  }

  renderPagination(pageCount) {
    this.pagination.innerHTML = ""
    this.pagination.append(
      this.paginationButton(language.previous, this.page - 1, this.page === 1),
      this.paginationButton(language.next, this.page + 1, this.page === pageCount)
    )
  }

  paginationButton(label, page, disabled) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "btn btn-sm btn-light"
    button.textContent = label
    button.disabled = disabled
    button.addEventListener("click", () => {
      this.page = page
      this.render()
    })

    return button
  }

  cellText(row, index) {
    return row.cells[index].textContent.trim()
  }

  get filteredLabel() {
    return this.rows.length === this.filteredRows().length
      ? ""
      : ` ${language.infoFiltered.replace("%{max}", this.rows.length)}`
  }
}
